import {Button, toast, useMediaQuery} from '@cashiaApp/web-components';
import Payment from 'payment';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';

import {
  session,
  SessionResponse,
  prepareCardCheckout,
  PrepareCardCheckoutData,
  PrepareCardCheckoutResponse,
  stepUpCardCheckout,
  StepUpCardCheckoutData,
  mpesaCheckout,
  MpesaCheckoutData,
  CheckoutTxnQueryData,
  TransactionResponse,
  transactionPolling,
} from './api';
import CardPay from './components/card';
import Inactive from './components/inactive';
import MPesaPay from './components/mpesa';
import PaymentStatus from './components/paymentStatus';
import processingGif from '../../assets/animations/processing.gif';
import {ReactComponent as ShopIcon} from '../../assets/icons/sidebar/shop.svg';
import {ReactComponent as CashiaLogo} from '../../assets/logos/cashia_full_logo_red.svg';
import logo from '../../assets/logos/cashia_logo_pink.svg';
import {ReactComponent as MastercardLogo} from '../../assets/logos/mastercard.svg';
import {ReactComponent as MpesaLogo} from '../../assets/logos/mpesa.svg';
import {ReactComponent as MpesaSmallLogo} from '../../assets/logos/mpesa_small.svg';
import {ReactComponent as PciLogo} from '../../assets/logos/pci.svg';
import {ReactComponent as VisaLogo} from '../../assets/logos/visa.svg';
import Tabs, {Tab} from '../../components/Tabs';
import LoadingModal from '../../components/common/LoadingModal';
import Modal from '../../components/tailwind/Modal';
import Spinner from '../../components/tailwind/Spinner';
import {apiUrl} from '../../utils/constants';
import {cn} from '../../utils/reusableFunctions';
import validateKenyanPhoneNumber from '../../utils/validatePhoneNumber';

type Props = {
  method?: 'card' | 'mpesa';
};

export type CardInfo = {
  firstName: string;
  lastName: string;
  email: string;
  number: string;
  expiration: string;
  cvvcode: string;
  address1?: string;
  country?: string;
  administrativeArea?: string;
  locality?: string;
  postalCode?: string;
};

type MessageData = {
  MessageType: string;
  SessionId: string;
  status: boolean;
};

const Footer = () => {
  return (
    <div className="font-[500] text-foggy flex justify-between px-10 max-md:px-0 max-md:mt-5 max-md:pt-[20%] max-md:pb-5 mt-5">
      <p className="max-md:text-[13px]">
        <a href="https://www.cashia.com/">
          Powered by <span className="text-smoothRed font-bold">Cashia</span>
        </a>
      </p>
      <div className="flex gap-4 max-md:gap-2">
        <p className="border-r-[1px] border-greyish pr-6 max-md:pr-2 max-md:text-[12px]">
          Terms & conditions
        </p>
        <p className="max-md:text-[12px]">Privacy policy</p>
      </div>
    </div>
  );
};

export default function PaymentView({method = 'mpesa'}: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const {origin} = new URL(window.location.href);
  const {businessName, reference} = useParams();
  const [sessionData, setSessionData] = useState<SessionResponse | null>(null);
  const [mpesaTabVisible, setMpesaTabVisible] = useState(true);
  const [cardTabVisible, setCardTabVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [cardData, setCardData] = useState<CardInfo>({
    firstName: '',
    lastName: '',
    email: '',
    number: '',
    expiration: '',
    cvvcode: '',
    country: '',
    address1: '',
    administrativeArea: '',
    locality: '',
    postalCode: '',
  });
  const [mpesaModalOpen, setMpesaModalOpen] = useState(false);
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [cardOrderLoading, setCardOrderLoading] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [isValidCardData, setIsValidCardData] = useState(false);
  const [prepareCardCheckoutResponse, setPrepareCardCheckoutResponse] =
    useState<PrepareCardCheckoutResponse | null>(null);
  const stepUpRef = useRef(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasRun = useRef(false);
  const [isInAfrica, setIsInAfrica] = useState(true);
  const [transactionId, setTransactionId] = useState('');
  const [transactionResponse, setTransactionResponse] =
    useState<TransactionResponse>();
  const [isPolling, setIsPolling] = useState(true);

  useEffect(() => {
    if (hasRun.current) return;
    const loadSession = async () => {
      try {
        const response = await session(reference || '');
        if (response.currency == 'KES' && response.amount > 250000) {
          setMpesaTabVisible(false);
          if (!location.pathname.includes('card')) {
            navigate(`${location.pathname}/card`);
          }
        }
        if (response.currency == 'KES' && response.amount < 100) {
          setCardTabVisible(false);
        }
        setSessionData(response);
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error('An unknown error occurred.');
        }
      } finally {
        setLoading(false);
      }
    };

    void loadSession(); // Explicitly ignore the promise returned by loadSession
    hasRun.current = true;
  }, []); // Empty dependency array ensures this runs once on component mount

  const baseLink = `/link/${
    businessName?.toLocaleLowerCase().trim().replace(/\s+/g, '-') || ''
  }/${reference || ''}`;

  const tabs = useMemo(() => {
    const newURL = new URL(`${origin}`);
    newURL.pathname = baseLink;
    let activeTabs: Tab[] = [
      {
        name: 'M-Pesa',
        link: `${newURL.pathname}`,
      },
      {
        name: 'Card',
        link: `${newURL.pathname}/card`,
      },
    ];
    if (!mpesaTabVisible) {
      activeTabs = activeTabs.filter((tab, index) => index !== 0);
    }
    if (!cardTabVisible) {
      activeTabs = activeTabs.filter(
        (tab, index) => index !== activeTabs.length - 1
      );
    }
    return activeTabs;
  }, [origin, baseLink, mpesaTabVisible, cardTabVisible]);

  const btnDisabled = useMemo(() => {
    if (method === 'mpesa') {
      return !validateKenyanPhoneNumber(phoneNumber) || !phoneNumber;
    } else {
      return !isValidCardData;
    }
  }, [phoneNumber, method, isValidCardData]);

  const handlePhoneNumberChange = (val: string) => {
    setPhoneNumber(val);
    if (!validateKenyanPhoneNumber(val) || !/^[0-9]+$/.test(val)) {
      setErrorMessage('Please enter a valid kenyan number');
    } else {
      setErrorMessage('');
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      if (method == 'mpesa') {
        const checkoutData: MpesaCheckoutData = {
          phoneNumber: '254' + phoneNumber,
          description: 'Mpesa Payment',
          secureToken: reference || '',
        };
        const response = await mpesaCheckout(checkoutData);
        setTransactionId(response.transactionId || '');
        setShowStatus(true);
        setMpesaModalOpen(true);
      }
      if (method == 'card') {
        const checkoutData: PrepareCardCheckoutData = {
          cardHolderName: cardData.firstName + ' ' + cardData.lastName,
          cardNo: cardData.number.replace(/\s/g, ''),
          cardType: Payment.fns.cardType(cardData.number),
          expireMonth: cardData.expiration?.slice(0, 2) || '00',
          expireYear: `${
            parseInt(cardData.expiration?.slice(3, 5) || '', 10) + 2000
          }`,
          secureToken: reference || '',
        };
        const response = await prepareCardCheckout(checkoutData);
        setShowStatus(true);
        setCardModalOpen(true);
        setCardOrderLoading(true);
        setPrepareCardCheckoutResponse(response);
      }
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('An unknown error occurred.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (prepareCardCheckoutResponse?.flowData?.actionUrl) {
      const collectionForm = document.getElementById(
        'cardinal_collection_form'
      ) as HTMLFormElement;

      if (collectionForm) {
        // Add a loading state before form submission
        setCardOrderLoading(true);

        // Create a timeout to clear hanging state
        const timeoutId = setTimeout(() => {
          setCardOrderLoading(false);
          console.warn('Cardinal form submission timed out');
        }, 60000); // 60 second timeout

        // Submit the form
        collectionForm.submit();

        const loadHandler = (event: MessageEvent) => {
          const eventOrigin = event.origin;
          let messageData: MessageData;

          try {
            messageData =
              typeof event.data === 'string'
                ? (JSON.parse(event.data) as MessageData)
                : (event.data as MessageData);

            if (
              eventOrigin === process.env.REACT_APP_API_CENTINEL_URL &&
              messageData?.MessageType === 'profile.completed'
            ) {
              // Clear the timeout since we got a response
              clearTimeout(timeoutId);

              // Process the stepUp checkout after a short delay
              setTimeout(async () => {
                try {
                  const checkoutData: StepUpCardCheckoutData = {
                    cardHolderName:
                      cardData.firstName + ' ' + cardData.lastName,
                    cardNo: cardData.number.replace(/\s/g, ''),
                    cardType: Payment.fns.cardType(cardData.number),
                    expireMonth: cardData.expiration?.slice(0, 2) || '00',
                    expireYear: `${
                      parseInt(cardData.expiration?.slice(3, 5) || '', 10) +
                      2000
                    }`,
                    securityCode: cardData.cvvcode,
                    secureToken: reference || '',
                    billTo: {
                      firstName: cardData.firstName,
                      lastName: cardData.lastName,
                      email: cardData.email,
                    },
                    feeAmount: 0,
                    flowData: {
                      referenceId:
                        prepareCardCheckoutResponse?.flowData?.referenceId,
                      token: prepareCardCheckoutResponse?.flowData?.token,
                      actionUrl: `${apiUrl}/v1/transaction/payment-result`,
                    },
                  };

                  if (!isInAfrica) {
                    checkoutData.billTo = {
                      ...checkoutData.billTo,
                      ...{
                        country: cardData.country,
                        locality: cardData.locality,
                        postalCode: cardData.postalCode,
                        address1: cardData.address1,
                        administrativeArea: cardData.administrativeArea,
                      },
                    };
                  }

                  const response = await stepUpCardCheckout(checkoutData);
                  setTransactionId(response?.transactionId || '');
                  const stepUpFlowData = response.flowData;

                  const stepUpForm = document.getElementById(
                    'step-up-form'
                  ) as HTMLFormElement;

                  if (stepUpForm) {
                    const jwtInput = document.getElementById(
                      'step-up-jwt'
                    ) as HTMLInputElement;
                    const mdInput = document.getElementById(
                      'step-up-md'
                    ) as HTMLInputElement;

                    if (jwtInput && mdInput) {
                      jwtInput.value = stepUpFlowData?.token || '';
                      mdInput.value = stepUpFlowData?.referenceId || '';
                      stepUpForm.action = stepUpFlowData?.actionUrl || '';

                      // Add a small delay before submitting the step-up form
                      setTimeout(() => {
                        stepUpForm.submit();
                        setCardOrderLoading(false);
                      }, 500);
                    }
                  }
                } catch (error) {
                  console.error('StepUp checkout error:', error);
                  setCardOrderLoading(false);
                  toast.error('An error occurred during payment processing');
                }
              }, 2000);
            }
          } catch (error) {
            console.error('Message processing error:', error);
          }
        };

        // Add message event listener
        window.addEventListener('message', loadHandler);

        // Cleanup function
        return () => {
          window.removeEventListener('message', loadHandler);
          clearTimeout(timeoutId);
        };
      }
    }
  }, [prepareCardCheckoutResponse, cardData, reference, isInAfrica]);

  const handleRedirect = (url: string) => {
    setTimeout(() => {
      window.location.href = `${url || ''}`;
    }, 5000);
  };

  useEffect(() => {
    // Initialize EventSource
    const eventSource = new EventSource(
      `${apiUrl}/v1/checkout/session/sse/${reference || ''}`
    );

    // Listen for incoming messages
    eventSource.onmessage = (event) => {
      let messageData: CheckoutTxnQueryData;
      if (typeof event.data === 'string') {
        try {
          messageData = JSON.parse(event.data) as CheckoutTxnQueryData;
        } catch {
          messageData = {} as CheckoutTxnQueryData;
        }
      } else {
        messageData = event.data as CheckoutTxnQueryData;
      }
      setTransactionResponse({
        currency: messageData?.Transaction?.currency || '',
        amount: messageData?.Transaction?.amount || 0,
        transactionId: messageData?.Transaction?.transaction_id || '',
        requestId: messageData?.Transaction?.request_id || '',
        status: messageData?.Transaction?.status || '',
        message: messageData?.Transaction?.message || '',
        transactionDate: messageData?.Transaction?.created_at || '',
      } as TransactionResponse);
    };
    // Handle errors
    eventSource.onerror = (err) => {
      console.error('SSE error:', err);
      eventSource.close();
    };

    // Cleanup on component unmount
    return () => {
      eventSource.close();
    };
  }, []);

  // Polling function
  useEffect(() => {
    if (!transactionId || !isPolling) return; // Exit early if no transactionId or polling is stopped

    const fetchData = async () => {
      try {
        const response = await transactionPolling(transactionId);
        if (response.status !== 'P') {
          setIsPolling(false); // Stop polling once the status is not 'P'
          setTransactionResponse(response);
          setCardModalOpen(false);
          setCardOrderLoading(false);
          setMpesaModalOpen(false);
          setIsSubmitting(false);
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    };

    // Fetch immediately on start
    void fetchData();

    // Polling every 1000ms (1 second) only if isPolling is true
    const intervalId = setInterval(() => {
      if (isPolling) {
        void fetchData();
      }
    }, 1000);

    // Cleanup function to clear interval when component unmounts or polling stops
    return () => clearInterval(intervalId);
  }, [transactionId, isPolling]);

  useEffect(() => {
    if (!transactionResponse || transactionResponse?.transactionId === '') {
      return;
    } // Prevent execution if no data
    setCardModalOpen(false);
    setCardOrderLoading(false);
    setMpesaModalOpen(false);
    setIsSubmitting(false);
    const baseUrl =
      transactionResponse.status === 'C'
        ? sessionData?.successCallbackUrl
        : sessionData?.errorCallbackUrl;
    if (!baseUrl) {
      console.error('Callback URL is undefined');
      return;
    }
    const url = `${baseUrl}?requestId=${transactionResponse?.requestId}&status=${transactionResponse?.status}`;
    handleRedirect(url);
  }, [transactionResponse, sessionData]);

  if (showStatus) {
    return (
      <>
        <PaymentStatus
          phoneNumber={phoneNumber}
          cardData={cardData}
          transactionResponse={transactionResponse}
          sessionData={sessionData}
          method={method}
          businessName={businessName}
          transactionId={transactionId}
        />
        <Modal
          isVisible={mpesaModalOpen}
          onClose={() => setMpesaModalOpen(false)}
          className="w-[90%] max-w-lg md:max-w-xl lg:max-w-2xl h-auto">
          <div>
            <div
              className="h-[200px] px-10 max-md:px-4 rounded-tr-[10px] rounded-tl-[10px]"
              style={{
                backgroundImage: `url(${processingGif})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
              }}>
              <div>
                <div className="flex gap-3 pt-[15%] mb-4 items-center">
                  <MpesaSmallLogo />
                  <p className="text-white font-[500]">
                    Pay {businessName} via MPESA
                  </p>
                </div>
                <p className="w-[310px] text-[13px] text-white font-[500]">
                  An STK push has been sent to +254 {phoneNumber}. Enter your
                  PIN to complete the payment.
                </p>
              </div>
            </div>
            <div className="px-10 max-md:px-4 py-5">
              <div className="flex w-full justify-between border-b-[1px] border-greyish py-6">
                <p className="font-[500] text-foggy">Amount:</p>
                <p className="font-[600]">
                  {new Intl.NumberFormat('en-US').format(
                    Number(sessionData?.amount || 0)
                  ) || '...'}
                </p>
              </div>
              <div className="flex w-full justify-between border-b-[1px] border-greyish py-6">
                <p className="font-[500] text-foggy">Method:</p>
                <p className="font-[600]">MPESA</p>
              </div>
              <div className="flex w-full justify-between py-6">
                <p className="font-[500] text-foggy">Mobile no:</p>
                <p className="font-[600]">+254 {phoneNumber}</p>
              </div>
            </div>
          </div>
        </Modal>
        {prepareCardCheckoutResponse?.flowData ? (
          <Modal
            isVisible={cardModalOpen}
            onClose={() => setCardModalOpen(false)}
            className={cn('md:w-[400px] md:h-[550px]', {
              'md:h-[120px]': cardOrderLoading,
            })}>
            {cardOrderLoading && (
              <div className="flex items-center flex-col">
                <Spinner className="mt-8 size-10 self-center w-full" />
                <p>Processing card details...</p>
              </div>
            )}
            <div>
              <iframe
                id="cardinal_collection_iframe"
                name="collectionIframe"
                height="10"
                width="10"
                style={{display: 'none'}}
              />
              <form
                id="cardinal_collection_form"
                method="POST"
                target="collectionIframe"
                action={prepareCardCheckoutResponse?.flowData?.actionUrl || ''}>
                <input
                  id="cardinal_collection_form_input"
                  type="hidden"
                  name="JWT"
                  value={prepareCardCheckoutResponse?.flowData?.token || ''}
                />
              </form>

              <iframe
                id="step-up-iframe"
                name="step-up-iframe"
                height={cardOrderLoading ? '0' : '530'}
                width="100%"
                style={{border: 'none'}}
                ref={stepUpRef}
              />
              <form id="step-up-form" target="step-up-iframe" method="POST">
                <input type="hidden" id="step-up-jwt" name="JWT" />
                <input type="hidden" id="step-up-md" name="MD" />
              </form>
            </div>
          </Modal>
        ) : (
          <div />
        )}
      </>
    );
  }

  if (loading) {
    return <LoadingModal open />;
  }

  return (
    <>
      <div className="flex flex-col min-h-screen">
        {sessionData?.requestId ? (
          <>
            <div className="h-full">
              <div>
                <div className="flex justify-between border-b-[1px] border-greyish px-4 items-center lg:h-[65px]">
                  <div className="flex gap-2 my-3 items-center">
                    <ShopIcon width={24} />
                    <p className="italic font-[600] max-md:text-[13px]">
                      {businessName || '---'}
                    </p>
                  </div>
                  <img src={logo} width={24} />
                </div>
              </div>
              <div className="px-14 max-md:px-4 flex h-full max-md:flex-col">
                <div className="max-md:w-full w-[50%] max-md:p-0 p-10 max-md:border-0 border-r-[1px] border-greyish">
                  <p className="text-[34px] max-md:text-[20px] my-3 font-[600]">
                    Pay {sessionData?.currency || ''}{' '}
                    {new Intl.NumberFormat('en-US').format(
                      Number(sessionData?.amount || 0)
                    ) || '...'}
                  </p>
                  <div className="border-greyish border-[1px] rounded-[5px] p-6 max-md:p-3 gap-8 flex flex-col">
                    <div className="flex justify-between overflow-hidden max-md:gap-5 gap-2">
                      {/* <p className="text-foggy font-[500]">Item:</p>
                    <p className="font-[500] max-w-[500px] max-md:text-[14px]">
                      {data?.paymentLink?.title || '---'}
                    </p> */}
                    </div>
                    <div className="flex justify-between">
                      <p className="text-foggy font-[500]">Pay to:</p>
                      <p className="font-[500] text-end max-md:text-[14px]">
                        {businessName || '---'}
                      </p>
                    </div>
                  </div>
                  {isDesktop && (
                    <div className="flex flex-col items-center max-md:items-start">
                      <p className="my-14 text-foggy max-md:text-[13px] max-md:w-[255px]">
                        Cashia is licenced and regulated by the Central Bank of
                        Kenya
                      </p>
                      <div className="w-full flex flex-col gap-10 max-md:gap-2 items-center max-md:items-start max-md:w-[255px]">
                        <div className="flex gap-10 max-md:gap-4 items-center">
                          <CashiaLogo className="max-md:w-[55px]" />
                          <MpesaLogo className="max-md:w-[60px]" />
                          <VisaLogo className="max-md:w-[50px]" />
                          <MastercardLogo className="max-md:w-[50px]" />
                        </div>
                        <PciLogo className="max-md:w-[50px]" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="max-md:w-full w-[50%] pt-14 px-16 max-md:p-0">
                  <p className="my-4 font-[600] text-[20px] max-md:text-[15px]">
                    Choose payment method below
                  </p>
                  <Tabs tabs={tabs} />
                  {method === 'card' ? (
                    <CardPay
                      cardData={cardData}
                      isInAfrica={isInAfrica}
                      setCardData={setCardData}
                      setIsValidCardData={setIsValidCardData}
                      setIsInAfrica={setIsInAfrica}
                    />
                  ) : (
                    <MPesaPay
                      phoneNumber={phoneNumber}
                      setPhoneNumber={handlePhoneNumberChange}
                      errorMessage={errorMessage}
                    />
                  )}
                  <div className="text-center">
                    <Button
                      onClick={handleSubmit}
                      className={cn(
                        'h-[52px] w-full bg-pink mt-3 active:scale-95',
                        {
                          'text-white bg-greyish cursor-default': btnDisabled,
                        }
                      )}
                      disabled={btnDisabled}>
                      {isSubmitting ? (
                        <Spinner className="fill-white" />
                      ) : (
                        <p className="font-[600]">Pay</p>
                      )}
                    </Button>
                  </div>
                </div>
                {!isDesktop && (
                  <div className="flex flex-col items-center max-md:items-start">
                    <p className="mt-8 text-foggy max-md:text-[13px] max-md:w-[230px]">
                      Cashia is licenced and regulated by the Central Bank of
                      Kenya
                    </p>
                    <div className="w-full flex flex-col gap-10 max-md:gap-1 items-center max-md:items-start max-md:w-[255px]">
                      <div className="flex gap-10 max-md:gap-4 items-center">
                        <CashiaLogo className="max-md:w-[65px]" />
                        <MpesaLogo className="max-md:w-[60px]" />
                        <VisaLogo className="max-md:w-[50px]" />
                        <MastercardLogo className="max-md:w-[50px]" />
                      </div>
                      <PciLogo className="max-md:w-[50px]" />
                    </div>
                  </div>
                )}
                {!isDesktop && <Footer />}
              </div>
            </div>
            {isDesktop && <Footer />}
          </>
        ) : (
          <Inactive />
        )}
      </div>
    </>
  );
}
